import './MapOverview.css';
import React, {useEffect, useRef, useState} from 'react';
import {get, getDatabase, ref} from 'firebase/database';
import MapComponent from './MapComponent';
import BuildingListItem from './BuildingListItem';
import {Helmet} from "react-helmet-async";
import CustomDropdown, {CustomDropdownOption} from "../Components/Dropdown";
import {Button} from "react-bootstrap";
import {BuildingSummary, CoordinatesModel} from "../types/protocols";

interface LocationCategories {
    category1?: Category;
    category2?: Category;
    category3?: Category;
    category4?: Category;
    category5?: Category;
    labels: {
        category1: string;
        category2: string;
        category3: string;
        category4: string;
        category5: string;
    };
}

interface Category {
    [key: string]: {
        name: string;
        parent?: string;
    };
}

export interface MapOverviewProps {}

const MapOverview: React.FC<MapOverviewProps> = () => {
    // ------------------------ State ------------------------
    const [locationCategories, setLocationCategories] = useState<LocationCategories | null>(null);

    // Buildings data
    const [buildings, setBuildings] = useState<BuildingSummary[] | undefined>(undefined);
    const [filteredBuildings, setFilteredBuildings] = useState<BuildingSummary[]>([]);

    // Category filters
    const [selectedCategory1, setSelectedCategory1] = useState<string>('');
    const [selectedCategory2, setSelectedCategory2] = useState<string>('');
    const [selectedCategory3, setSelectedCategory3] = useState<string>('');
    const [selectedCategory4, setSelectedCategory4] = useState<string>('');
    const [selectedCategory5, setSelectedCategory5] = useState<string>('');

    // Highlight logic
    const [highlightedKey, setHighlightedKey] = useState<string>('');
    const listItemRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});
    const [popupInfo, setPopupInfo] = useState<{
        id: string;
        coordinates: [number, number];
    } | null>(null);
    const [shouldRefocusMap, setShouldRefocusMap] = useState(true);

    // ------------------------ Effects ------------------------
    useEffect(() => {
        fetchLocationCategories().then(r => {});
        fetchBuildings().then(r => {});
    }, []);

    useEffect(() => {
        if (!buildings) return;
        const nextFiltered = filterBuildings(buildings, {
            selectedCategory1,
            selectedCategory2,
            selectedCategory3,
            selectedCategory4,
            selectedCategory5,
        });
        setFilteredBuildings(nextFiltered);
    }, [
        buildings,
        selectedCategory1,
        selectedCategory2,
        selectedCategory3,
        selectedCategory4,
        selectedCategory5,
    ]);

    useEffect(() => {
        if (!highlightedKey) return;
        const element = listItemRefs.current[highlightedKey];
        if (!element) return;
        const rect = element.getBoundingClientRect();
        const isInView =
            rect.top >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

        if (!isInView) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [highlightedKey]);

    // ------------------------ Data Fetching ------------------------
    const fetchLocationCategories = async () => {
        try {

            const db = getDatabase();
            const categoryRef = ref(db, 'locationCategories');
            const snapshot = await get(categoryRef);

            if (snapshot.exists()) {
                const data = snapshot.val() as LocationCategories;
                setLocationCategories(data);
            } else {
                console.log('No location categories data available in the snapshot');
            }
        } catch (err) {
            console.error('Failed to load categories data', err);
        }
    };

    const fetchBuildings = async () => {
        try {

            const db = getDatabase();
            const buildingsRef = ref(db, 'buildingsSummary');
            const snapshot = await get(buildingsRef);

            if (snapshot.exists()) {
                const data = snapshot.val() as { [key: string]: BuildingSummary };
                const buildingPromises = Object.entries(data).map(async ([key, buildingData]) => {
                    return {...buildingData} as BuildingSummary;
                });

                const buildingsArray = await Promise.all(buildingPromises);
                setBuildings(buildingsArray);
            } else {
                console.log('No buildings data available in the snapshot');
            }
        } catch (err) {
            console.error('Failed to load building data', err);
        }
    };

    // ------------------------ Helpers & Handlers ------------------------
    const filterBuildings = (
        allBuildings: BuildingSummary[],
        {
            selectedCategory1,
            selectedCategory2,
            selectedCategory3,
            selectedCategory4,
            selectedCategory5,
        }: {
            selectedCategory1: string;
            selectedCategory2: string;
            selectedCategory3: string;
            selectedCategory4: string;
            selectedCategory5: string;
        }
    ): BuildingSummary[] => {
        return allBuildings.filter((building) => {
            const categories = building.locationCategories;
            return (
                (!selectedCategory1 || categories?.category1?.key === selectedCategory1) &&
                (!selectedCategory2 || categories?.category2?.key === selectedCategory2) &&
                (!selectedCategory3 || categories?.category3?.key === selectedCategory3) &&
                (!selectedCategory4 || categories?.category4?.key === selectedCategory4) &&
                (!selectedCategory5 || categories?.category5?.key === selectedCategory5)
            );
        });
    };

    const getOptions = (category: Category | undefined, parentKey?: string): CustomDropdownOption[] => {
        if (!category) return [];
        return Object.entries(category)
            .filter(([_, value]) => value.parent === parentKey)
            .map(([key, value]) => ({ key, name: value.name }));
    };

    /**
     * Reset categories from `level` onward.
     * e.g. if level=2, reset category2..5.
     */
    const resetSubCategories = (level: number) => {
        if (level <= 1) {
            setSelectedCategory1('');
        }
        if (level <= 2) {
            setSelectedCategory2('');
        }
        if (level <= 3) {
            setSelectedCategory3('');
        }
        if (level <= 4) {
            setSelectedCategory4('');
        }
        if (level <= 5) {
            setSelectedCategory5('');
        }
    };

    // ------------------------ Derived Data ------------------------
    const category1Options = getOptions(locationCategories?.category1);
    const category2Options = getOptions(locationCategories?.category2, selectedCategory1);
    const category3Options = getOptions(locationCategories?.category3, selectedCategory2);
    const category4Options = getOptions(locationCategories?.category4, selectedCategory3);
    const category5Options = getOptions(locationCategories?.category5, selectedCategory4);

    const filteredCoordinates = filteredBuildings
        .filter(
            (building): building is BuildingSummary & { coordinates: CoordinatesModel } =>
                building.coordinates !== undefined && building.coordinates !== null
        )
        .map((building) => ({
            coords: [building.coordinates.latitude, building.coordinates.longitude] as [number, number],
        }));

    const allCoordinates = (buildings ?? [])
        .filter((b) => b.coordinates)
        .map((b) => ({
            coords: [b.coordinates!.latitude, b.coordinates!.longitude] as [number, number],
            building: b,
            isFiltered: filteredBuildings.some((fb) => fb.key === b.key),
        }));

    // ------------------------ Render ------------------------
    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Helmet>
                <title>Byggnader</title>
            </Helmet>

            <div
                style={{
                    padding: '20px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-end',
                    gap: '20px',
                    flexWrap: 'wrap',
                    minHeight: '122px',
                }}
            >
            {
                locationCategories &&
                <>
                    {/* Category 1 */}
                    <CustomDropdown
                        label={locationCategories?.labels.category1 || 'Kategori 1'}
                        options={category1Options}
                        selectedKey={selectedCategory1}
                        onSelect={(key) => {
                            setSelectedCategory1(key);
                            resetSubCategories(2);
                            setShouldRefocusMap(true);
                        }}
                        disabled={false}
                    />

                    {locationCategories?.labels.category2 && (
                        <CustomDropdown
                            label={locationCategories.labels.category2}
                            options={category2Options}
                            selectedKey={selectedCategory2}
                            onSelect={(key) => {
                                setSelectedCategory2(key);
                                resetSubCategories(3);
                                setShouldRefocusMap(true);
                            }}
                            disabled={!selectedCategory1}
                        />
                    )}

                    {locationCategories?.labels.category3 && (
                        <CustomDropdown
                            label={locationCategories.labels.category3}
                            options={category3Options}
                            selectedKey={selectedCategory3}
                            onSelect={(key) => {
                                setSelectedCategory3(key);
                                resetSubCategories(4);
                                setShouldRefocusMap(true);
                            }}
                            disabled={!selectedCategory2}
                        />
                    )}

                    {locationCategories?.labels.category4 && (
                        <CustomDropdown
                            label={locationCategories.labels.category4}
                            options={category4Options}
                            selectedKey={selectedCategory4}
                            onSelect={(key) => {
                                setSelectedCategory4(key);
                                resetSubCategories(5);
                                setShouldRefocusMap(true);
                            }}
                            disabled={!selectedCategory3}
                        />
                    )}

                    {locationCategories?.labels.category5 && (
                        <CustomDropdown
                            label={locationCategories.labels.category5}
                            options={category5Options}
                            selectedKey={selectedCategory5}
                            onSelect={(key) => {
                                setSelectedCategory5(key);
                                setShouldRefocusMap(true);
                            }}
                            disabled={!selectedCategory4}
                        />
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Button
                            style={{
                                minWidth: '100px',
                                display: 'flex',
                                flexDirection: 'column',
                                marginBottom: '1rem',
                            }}
                            variant="secondary"
                            disabled={selectedCategory1===""}
                            onClick={() => {
                                resetSubCategories(1);
                                setShouldRefocusMap(true);
                            }}
                        >
                            <i className="bi bi-x-circle" style={{ marginRight: '8px' }}></i>
                            Rensa filter
                        </Button>
                    </div>
                </>
            }
            </div>

            <div style={{
                display: 'flex',
                flex: 1,
                overflow: 'hidden',
            }}>
                {/* Building List */}
                <div
                    style={{
                        width: '340px',
                        overflowY: 'auto',
                        maxHeight: '100%',
                    }}
                >
                    <ul
                        style={{
                            listStyleType: 'none',
                            padding: 0,
                            paddingBottom: '1rem',
                            margin: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem'
                        }}
                    >
                        {filteredBuildings.map((building) => (
                            <BuildingListItem
                                key={building.key}
                                building={building}
                                highlighted={highlightedKey === building.key}
                                onClick={() => {
                                    setShouldRefocusMap(false);
                                    setHighlightedKey(building.key!);
                                    if (building.coordinates?.latitude) {
                                        setPopupInfo({
                                            id: building.key!,
                                            coordinates: [
                                                building.coordinates!.latitude,
                                                building.coordinates!.longitude,
                                            ],
                                        });
                                    } else {
                                        setPopupInfo({
                                            id: building.key!,
                                            coordinates: [0, 0],
                                        });
                                    }
                                }}
                                ref={(el) => (listItemRefs.current[building.key!] = el)}
                            />
                        ))}
                    </ul>
                </div>

                {/* Map */}
                <div className={"map_container"} >
                    <MapComponent
                        allCoordinates={allCoordinates}
                        focusCoordinates={filteredCoordinates}
                        highlightedId={highlightedKey}
                        popupInfo={popupInfo}
                        shouldResizeBounds={shouldRefocusMap}
                        buildingswithCovers={buildings}
                        onMarkerClick={(key) => {
                            setShouldRefocusMap(false);
                            setHighlightedKey(key);
                            const building = buildings?.find((b) => b.key === key);
                            if (building?.coordinates) {
                                setPopupInfo({
                                    id: key,
                                    coordinates: [
                                        building.coordinates.latitude,
                                        building.coordinates.longitude,
                                    ],
                                });
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default MapOverview;
